import React, { createContext, useContext, useState, useEffect } from 'react';
import API from '../api/axios';

const AuthContext = createContext(null);

// Some browser contexts (Safari Private Browsing, certain in-app browsers
// like Instagram/Facebook/TikTok's embedded webview, hardened privacy
// browsers) block or throw on localStorage access entirely. Without this
// wrapper, any localStorage call in those contexts throws an uncaught
// SecurityError and crashes the whole app to a blank page. These helpers
// let the app keep working for that session — the user just won't stay
// logged in across page reloads, which is a much better failure mode than
// a blank page.
function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
}

function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // Ignore — nothing to clean up if storage isn't accessible anyway.
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(null);

  useEffect(() => {
    const storedUser = safeGetItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        setLoading(false);
      } catch (e) {
        safeRemoveItem('user');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #8B0D1F 0%, #C8102E 100%)',
        color: 'white',
        fontSize: 32,
        fontWeight: 700
      }}>
        Godly Match
      </div>
    );
  }

  const login = (userData, token, refreshToken) => {
    setUser(userData);
    setHasProfile(false);
    safeSetItem('user', JSON.stringify(userData));
    safeSetItem('token', token);
    if (refreshToken) {
      safeSetItem('refresh_token', refreshToken);
    }
    API.get('/api/profile/me')
      .then(() => setHasProfile(true))
      .catch(() => setHasProfile(false));
  };

  const logout = async () => {
    const refreshToken = safeGetItem('refresh_token');
    if (refreshToken) {
      try {
        await API.post('/api/auth/logout', { refresh_token: refreshToken });
      } catch (e) {
        // Ignore, still clear locally
      }
    }
    setUser(null);
    safeRemoveItem('user');
    safeRemoveItem('token');
    safeRemoveItem('refresh_token');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasProfile, setHasProfile, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}